/**
 * Einen Servicefall eröffnen: Kopf plus erster Servicepunkt.
 *
 * Der Kopf sagt, was der Kunde meldet, wann, über welchen Kanal und zu
 * welcher Bestellung. Ohne ersten Punkt gibt es keinen Fall – ein Servicefall
 * ohne Maßnahme wäre nur eine Beschwerde ohne Adresse.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer, Pflicht, PflichtLegende } from '../komponenten/Modal';
import { ServicepunktFelder, punktFehlt, punktKoerper, type PunktWerte } from './ServicepunktModal';
import type { Angebot, AngebotAntwort } from '../typen';

const KANAELE = ['Telefon', 'E-Mail', 'Baustelle', 'Übergabeprotokoll', 'Sonstiges'];

export function ServicefallModal({
  angebot,
  onSchliessen,
  onFertig,
}: {
  angebot: Angebot;
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const { toastZeigen } = useKora();
  const meta = useMeta();
  const [titel, setTitel] = useState('');
  const [kanal, setKanal] = useState('Telefon');
  const [gemeldetAm, setGemeldetAm] = useState(meta.heute);
  const [bestellungId, setBestellungId] = useState<string>(
    angebot.bestellungen.length === 1 ? String(angebot.bestellungen[0]!.id) : '',
  );
  const [punkt, setPunkt] = useState<PunktWerte>({
    massnahme: '',
    bezug: '',
    notiz: '',
    termin: '',
    zeitfenster: '',
    verantwortlich: angebot.kalkulant,
  });
  const [fehler, setFehler] = useState('');

  const fehlt = [...(titel.trim() ? [] : ['Titel']), ...punktFehlt(punkt)];
  const rot = (feld: string) => (fehlt.includes(feld) ? 'modal-eingabe--fehler' : '');

  const mutation = useMutation({
    mutationFn: () =>
      api.sende<AngebotAntwort>(`/service/angebote/${angebot.id}`, {
        titel: titel.trim(),
        kanal,
        gemeldetAm,
        bestellungId: bestellungId ? Number(bestellungId) : null,
        punkt: punktKoerper(punkt),
      }),
    onSuccess: (a) => {
      onFertig();
      toastZeigen(a.toast ?? 'Servicefall eröffnet.');
      onSchliessen();
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  return (
    <Modal
      titel="Servicefall eröffnen"
      untertitel={angebot.name}
      breite={560}
      fest={mutation.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={mutation.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer onClick={() => mutation.mutate()} disabled={mutation.isPending || fehlt.length > 0}>
            {mutation.isPending ? 'Wird eröffnet …' : 'Servicefall eröffnen'}
          </KnopfPrimaer>
        </>
      }
    >
      <label className="modal-feld">
        <span>Was meldet der Kunde?<Pflicht /></span>
        <input
          className={`modal-eingabe ${rot('Titel')}`}
          value={titel}
          autoFocus
          placeholder="z. B. Motor der Zip-Screen läuft nicht mehr"
          onChange={(e) => {
            setTitel(e.target.value);
            setFehler('');
          }}
        />
      </label>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <label className="modal-feld" style={{ flex: '0 0 170px' }}>
          <span>Gemeldet am</span>
          <input
            type="date"
            className="modal-eingabe"
            value={gemeldetAm}
            max={meta.heute}
            onChange={(e) => setGemeldetAm(e.target.value)}
          />
        </label>
        <label className="modal-feld" style={{ flex: 1, minWidth: 150 }}>
          <span>Über</span>
          <select className="modal-eingabe" value={kanal} onChange={(e) => setKanal(e.target.value)}>
            {KANAELE.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
      </div>

      {angebot.bestellungen.length > 0 && (
        <label className="modal-feld">
          <span>Bezug auf Bestellung · damit Ersatzteile beim richtigen Lieferanten landen</span>
          <select className="modal-eingabe" value={bestellungId} onChange={(e) => setBestellungId(e.target.value)}>
            <option value="">– ohne Bezug –</option>
            {angebot.bestellungen.map((b) => (
              <option key={b.id} value={b.id}>
                Bestellung {b.nr}
                {b.lieferant ? ` · ${b.lieferant}` : ''}
                {b.umfang ? ` · ${b.umfang}` : ''}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="modal-feld" style={{ marginTop: 6 }}>
        <span style={{ color: 'var(--eh-text)' }}>Erster Servicepunkt</span>
      </div>
      <ServicepunktFelder
        werte={punkt}
        setWerte={(w) => {
          setPunkt(w);
          setFehler('');
        }}
        kalkulant={angebot.kalkulant}
        rot={rot}
      />

      {fehlt.length > 0 && (
        <div className="modal-hinweis" style={{ color: 'var(--eh-rot)', fontWeight: 600 }}>
          Es fehlt noch: {fehlt.join(', ')}.
        </div>
      )}
      <PflichtLegende />
      {fehler && <div className="modal-fehler">{fehler}</div>}
    </Modal>
  );
}
